import {controller, target} from '@github/catalyst'
import {requestSubmit} from '@github-ui/form-utils'
import {decode, encode, encrypt} from '../behaviors/encrypt'
import type {
  SelectPanelExperimentalElement,
  ItemActivatedEvent,
} from '../../../../../app/components/primer/experimental/select-panel-element'
import type {ActionMenuElement} from '@primer/view-components/app/components/primer/alpha/action_menu/action_menu_element'
import {updateSearchParams} from '@github-ui/history'

@controller('new-codespace')
class NewCodespaceElement extends HTMLElement {
  @target declare declarativeSecretsHash: HTMLInputElement
  @target declare vscsTargetUrl: HTMLInputElement
  @target declare loadingVscode: HTMLElement
  @target declare vscodePoller: HTMLElement
  @target declare advancedOptionsForm: HTMLFormElement | undefined
  @target declare skuNameInput: HTMLInputElement | undefined
  @target declare repositorySelectPanel: SelectPanelExperimentalElement
  @target declare repoInput: HTMLInputElement | undefined
  @target declare geoInput: HTMLInputElement | undefined

  secrets_hash = new Map() as Map<string, string>
  #abortController!: AbortController

  async connectedCallback() {
    // If we just force-reloaded the page because of an error, drop the param so the flash doesn't stick.
    const url = new URL(document.location.href, window.location.origin)
    const urlParams = new URLSearchParams(url.search)

    if (urlParams.has('response_error')) {
      urlParams.delete('response_error')
      updateSearchParams(urlParams)
    }

    const {signal} = (this.#abortController = new AbortController())
    this.addEventListener('itemActivated', this, {signal})
  }

  disconnectedCallback() {
    this.#abortController.abort()
  }

  handleEvent(event: Event) {
    if (event.type === 'itemActivated' && event.target === this.repositorySelectPanel) {
      if (this.advancedOptionsForm && this.repoInput) {
        const activationEvent = event as CustomEvent<ItemActivatedEvent>
        const li = activationEvent.detail.item as HTMLElement
        const value = li.querySelector('[data-value]')?.getAttribute('data-value')
        if (value) {
          this.repoInput.value = value
        }
        requestSubmit(this.advancedOptionsForm)
      }
    }
  }

  toggleLoadingVscode() {
    const isHidden = this.loadingVscode.hidden
    const children = this.children
    for (let i = 0; i < children.length; i++) {
      ;(children[i] as HTMLElement).hidden = isHidden
    }
    this.loadingVscode.hidden = !isHidden
  }

  machineTypeSelected(event: Event) {
    const button = event.currentTarget as HTMLInputElement
    const skuName = button.getAttribute('data-sku-name')
    if (this.skuNameInput && skuName) this.skuNameInput.value = skuName
    if (this.advancedOptionsForm) {
      requestSubmit(this.advancedOptionsForm)
    }
  }

  pollForVscode(event: CustomEvent) {
    this.toggleLoadingVscode()
    const pollingUrl = (event.currentTarget as HTMLElement).getAttribute('data-src')
    if (pollingUrl) this.vscodePoller.setAttribute('src', pollingUrl)
  }

  vscsTargetUrlUpdated(event: Event) {
    const element = event.currentTarget as HTMLInputElement
    this.vscsTargetUrl.value = element.value
  }

  geoUpdated(event: Event) {
    const element = event.currentTarget as ActionMenuElement
    const selectedGeo = element.selectedItems[0]?.value
    if (this.geoInput && selectedGeo) {
      this.geoInput.value = selectedGeo
    }
    if (this.advancedOptionsForm) {
      requestSubmit(this.advancedOptionsForm)
    }
  }

  async declarativeSecretsHashUpdated(event: Event) {
    const form = event.currentTarget as HTMLInputElement

    const secretName = form.getAttribute('data-name')
    if (!secretName) return

    let secretPlainText = form.value
    const publicKey = decode(form.getAttribute('data-public-key')!)

    // For existing secrets, unsets the secret value if the box is unchecked
    if (form.getAttribute('type') === 'checkbox' && !form.checked) {
      secretPlainText = ''
    }

    // Update the hash with the encrypted secret or delete if no value is provided
    if (!secretPlainText) {
      this.secrets_hash.delete(secretName)
    } else {
      this.secrets_hash.set(secretName, encode(await encrypt(publicKey, secretPlainText)))
    }

    this.declarativeSecretsHash.value = JSON.stringify(Object.fromEntries(this.secrets_hash))
  }
}
